import { v4 as uuidv4 } from 'uuid'
import { Product, Order, User, SCoinTransaction, APP_CONFIG } from '../types'

// Local database simulation using localStorage
// This will be replaced with actual D1 calls when deployed

const STORAGE_KEYS = {
    PRODUCTS: 'sm_db_products',
    ORDERS: 'sm_db_orders',
    USERS: 'sm_db_users',
    TRANSACTIONS: 'sm_db_transactions'
}

// Initialize with sample data
function initializeDB() {
    if (!localStorage.getItem(STORAGE_KEYS.PRODUCTS)) {
        const sampleProducts: Product[] = [
            {
                id: uuidv4(),
                name: 'Elegant Silk Saree - Royal Blue',
                description: 'Beautiful handwoven silk saree with golden zari border, perfect for weddings and special occasions.',
                category: 'Sarees',
                mrp: 4999,
                sm_price: 4499,
                stock: 15,
                barcode: 'SMF001',
                image_url: 'https://images.unsplash.com/photo-1610030469983-98e550d6193c?w=400',
                created_at: new Date().toISOString()
            },
            {
                id: uuidv4(),
                name: 'Designer Anarkali Kurti - Maroon',
                description: 'Stunning floor-length Anarkali with intricate embroidery and dupatta.',
                category: 'Kurtis',
                mrp: 2499,
                sm_price: 2249,
                stock: 20,
                barcode: 'SMF002',
                image_url: 'https://images.unsplash.com/photo-1594463750939-ebb28c3f7f75?w=400',
                created_at: new Date().toISOString()
            },
            {
                id: uuidv4(),
                name: 'Bridal Lehenga - Pink & Gold',
                description: 'Exquisite bridal lehenga with heavy embroidery and sequin work.',
                category: 'Lehengas',
                mrp: 15999,
                sm_price: 14399,
                stock: 5,
                barcode: 'SMF003',
                image_url: 'https://images.unsplash.com/photo-1583391733956-4c0b5c5e7b0e?w=400',
                created_at: new Date().toISOString()
            },
            {
                id: uuidv4(),
                name: 'Cotton Printed Kurti - Yellow',
                description: 'Comfortable everyday wear cotton kurti with traditional block prints.',
                category: 'Kurtis',
                mrp: 899,
                sm_price: 809,
                stock: 50,
                barcode: 'SMF004',
                image_url: 'https://images.unsplash.com/photo-1583391733981-8b530e5e5e44?w=400',
                created_at: new Date().toISOString()
            },
            {
                id: uuidv4(),
                name: 'Palazzo Suit Set - Teal',
                description: 'Trendy palazzo suit with embroidered kurta and matching dupatta.',
                category: 'Suits',
                mrp: 1999,
                sm_price: 1799,
                stock: 25,
                barcode: 'SMF005',
                image_url: 'https://images.unsplash.com/photo-1614252234498-c39e9e0c8a08?w=400',
                created_at: new Date().toISOString()
            },
            {
                id: uuidv4(),
                name: 'Banarasi Silk Saree - Green',
                description: 'Authentic Banarasi silk with traditional motifs and rich pallu.',
                category: 'Sarees',
                mrp: 8999,
                sm_price: 8099,
                stock: 8,
                barcode: 'SMF006',
                image_url: 'https://images.unsplash.com/photo-1617627143750-d86bc21e42bb?w=400',
                created_at: new Date().toISOString()
            }
        ]
        localStorage.setItem(STORAGE_KEYS.PRODUCTS, JSON.stringify(sampleProducts))
    }

    if (!localStorage.getItem(STORAGE_KEYS.ORDERS)) {
        localStorage.setItem(STORAGE_KEYS.ORDERS, JSON.stringify([]))
    }

    if (!localStorage.getItem(STORAGE_KEYS.TRANSACTIONS)) {
        localStorage.setItem(STORAGE_KEYS.TRANSACTIONS, JSON.stringify([]))
    }
}

// Initialize on load
initializeDB()

// Products
export const localDb = {
    products: {
        getAll: (): Product[] => {
            const data = localStorage.getItem(STORAGE_KEYS.PRODUCTS)
            return data ? JSON.parse(data) : []
        },

        getByCategory: (category: string): Product[] => {
            const all = localDb.products.getAll()
            return all.filter(p => p.category === category)
        },

        getById: (id: string): Product | undefined => {
            const all = localDb.products.getAll()
            return all.find(p => p.id === id)
        },

        getByBarcode: (barcode: string): Product | undefined => {
            const all = localDb.products.getAll()
            return all.find(p => p.barcode === barcode)
        },

        create: (product: Omit<Product, 'id' | 'created_at'>): Product => {
            const all = localDb.products.getAll()
            const newProduct: Product = {
                ...product,
                id: uuidv4(),
                created_at: new Date().toISOString()
            }
            all.push(newProduct)
            localStorage.setItem(STORAGE_KEYS.PRODUCTS, JSON.stringify(all))
            return newProduct
        },

        update: (id: string, updates: Partial<Product>): Product | undefined => {
            const all = localDb.products.getAll()
            const index = all.findIndex(p => p.id === id)
            if (index === -1) return undefined

            all[index] = { ...all[index], ...updates }
            localStorage.setItem(STORAGE_KEYS.PRODUCTS, JSON.stringify(all))
            return all[index]
        },

        delete: (id: string): boolean => {
            const all = localDb.products.getAll()
            const filtered = all.filter(p => p.id !== id)
            if (filtered.length === all.length) return false

            localStorage.setItem(STORAGE_KEYS.PRODUCTS, JSON.stringify(filtered))
            return true
        },

        search: (query: string): Product[] => {
            const all = localDb.products.getAll()
            const lowerQuery = query.toLowerCase()
            return all.filter(p =>
                p.name.toLowerCase().includes(lowerQuery) ||
                p.description?.toLowerCase().includes(lowerQuery) ||
                p.category.toLowerCase().includes(lowerQuery) ||
                p.barcode?.toLowerCase().includes(lowerQuery)
            )
        }
    },

    orders: {
        getAll: (): Order[] => {
            const data = localStorage.getItem(STORAGE_KEYS.ORDERS)
            return data ? JSON.parse(data) : []
        },

        getByUserId: (userId: string): Order[] => {
            const all = localDb.orders.getAll()
            return all.filter(o => o.user_id === userId)
        },

        getById: (id: string): Order | undefined => {
            const all = localDb.orders.getAll()
            return all.find(o => o.id === id)
        },

        getByPickupCode: (code: string): Order | undefined => {
            const all = localDb.orders.getAll()
            return all.find(o => o.pickup_code === code)
        },

        create: (order: {
            user_id: string,
            items: Array<{ product_id: string; quantity: number; price: number; product_name: string }>,
            total: number,
            s_coins_used: number,
            payment_method: 'upi'
        }): Order => {
            const all = localDb.orders.getAll()
            const pickupCode = Math.random().toString(36).substr(2, 6).toUpperCase()

            const newOrder: Order = {
                id: uuidv4(),
                user_id: order.user_id,
                status: 'pending',
                total: order.total,
                s_coins_used: order.s_coins_used,
                s_coins_earned: Math.floor(order.total / 100) * APP_CONFIG.SCOINS_EARN_RATE,
                payment_method: order.payment_method,
                pickup_code: pickupCode,
                created_at: new Date().toISOString(),
                items: order.items.map(item => ({
                    id: uuidv4(),
                    order_id: '',
                    ...item
                }))
            }

            // Set order_id on items
            newOrder.items = newOrder.items?.map(item => ({ ...item, order_id: newOrder.id }))

            all.push(newOrder)
            localStorage.setItem(STORAGE_KEYS.ORDERS, JSON.stringify(all))
            return newOrder
        },

        updateStatus: (id: string, status: Order['status']): Order | undefined => {
            const all = localDb.orders.getAll()
            const index = all.findIndex(o => o.id === id)
            if (index === -1) return undefined

            all[index].status = status
            localStorage.setItem(STORAGE_KEYS.ORDERS, JSON.stringify(all))
            return all[index]
        }
    },

    transactions: {
        getAll: (): SCoinTransaction[] => {
            const data = localStorage.getItem(STORAGE_KEYS.TRANSACTIONS)
            return data ? JSON.parse(data) : []
        },

        getByUserId: (userId: string): SCoinTransaction[] => {
            const all = localDb.transactions.getAll()
            return all.filter(t => t.user_id === userId).sort((a, b) =>
                new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
            )
        },

        create: (transaction: Omit<SCoinTransaction, 'id' | 'created_at'>): SCoinTransaction => {
            const all = localDb.transactions.getAll()
            const newTransaction: SCoinTransaction = {
                ...transaction,
                id: uuidv4(),
                created_at: new Date().toISOString()
            }
            all.push(newTransaction)
            localStorage.setItem(STORAGE_KEYS.TRANSACTIONS, JSON.stringify(all))
            return newTransaction
        }
    },

    // S-Coins operations
    scoins: {
        earn: (userId: string, amount: number, orderId: string, description?: string): void => {
            // Update user balance
            const userStr = localStorage.getItem('sm_user')
            if (userStr) {
                const user = JSON.parse(userStr) as User
                user.s_coins = (user.s_coins || 0) + amount
                user.loyalty_points = (user.loyalty_points || 0) + amount
                localStorage.setItem('sm_user', JSON.stringify(user))
            }

            // Record transaction
            localDb.transactions.create({
                user_id: userId,
                amount,
                type: 'earn',
                order_id: orderId,
                description: description || `Earned from order`
            })
        },

        redeem: (userId: string, amount: number, orderId: string): boolean => {
            const userStr = localStorage.getItem('sm_user')
            if (!userStr) return false

            const user = JSON.parse(userStr) as User
            if ((user.s_coins || 0) < amount) return false

            user.s_coins = (user.s_coins || 0) - amount
            localStorage.setItem('sm_user', JSON.stringify(user))

            localDb.transactions.create({
                user_id: userId,
                amount: -amount,
                type: 'redeem',
                order_id: orderId,
                description: `Redeemed for order`
            })

            return true
        },

        purchase: (userId: string, amount: number): boolean => {
            const userStr = localStorage.getItem('sm_user')
            if (!userStr) return false

            const user = JSON.parse(userStr) as User
            user.s_coins = (user.s_coins || 0) + amount
            localStorage.setItem('sm_user', JSON.stringify(user))

            localDb.transactions.create({
                user_id: userId,
                amount,
                type: 'purchase',
                description: `Purchased ${amount} S-Coins`
            })

            return true
        },

        getBalance: (_userId: string): number => {
            const userStr = localStorage.getItem('sm_user')
            if (!userStr) return 0
            const user = JSON.parse(userStr) as User
            return user.s_coins || 0
        }
    }
}

// Utility to format currency
export function formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
    }).format(amount)
}

// Calculate SM Price from MRP
export function calculateSMPrice(mrp: number): number {
    return Math.round(mrp * (1 - APP_CONFIG.DISCOUNT_PERCENTAGE / 100))
}

// Generate UPI payment link
export function generateUPILink(amount: number, orderId: string): string {
    const upiId = APP_CONFIG.UPI_ID
    const name = APP_CONFIG.STORE_NAME
    const note = `Order ${orderId}`
    return `upi://pay?pa=${upiId}&pn=${encodeURIComponent(name)}&am=${amount}&cu=INR&tn=${encodeURIComponent(note)}`
}
